import {UploadFile} from "antd/lib/upload/interface";
import {FileUploader} from "digimuth-components/dist";
import {useInjection} from "inversify-react";
import React, {forwardRef, useEffect, useImperativeHandle, useRef} from "react";
import {Direction, ProductImageApi} from "../../../api";
import ApiService from "../../../services/apiService";
import {ItemRender} from "antd/es/upload/interface";
import {Button} from "antd";
import {ArrowDownOutlined, ArrowUpOutlined} from "@ant-design/icons";
import styles from "./productImagesUploader.module.scss";

type FileUploaderHandle = React.ElementRef<typeof FileUploader>;

function getSingleElementArrayOrEmpty<T>(element?: T): T[] {
    return element ? [element] : [];
}

const ProductsImagesUploader = forwardRef((props: ProductsFileUploaderProps, ref: React.ForwardedRef<any>) => {

    const fileUploaderRef = useRef<FileUploaderHandle>();
    const apiService = useInjection(ApiService);
    const fileApi = apiService.getApi(ProductImageApi);


    useEffect(() => {
        props.setFiles(props.defaultFileList!);
    }, [])

    useImperativeHandle(ref, () => ({
        async onFormFinish() {
            return await fileUploaderRef.current.onFormFinish();
        }
    }));

    const fileDownloadFunction = async (id: number) => {
        return await fileApi.apiProductImageIdFileStreamGet(id, {responseType: 'blob'});
    };

    const fileUploadFunction = async (file: UploadFile<any>, httpRequestOptions: any) => {
        let position = 0;
        for (let f of props.files) {
            if (f.uid === file.uid) {
                position = props.files.indexOf(f);
                break;
            }
        }
        return await fileApi.apiProductImagePost(position, file, undefined, undefined,
            undefined, undefined, undefined, undefined, undefined,
            undefined, httpRequestOptions
        );
    };

    const fileDeleteFunction = props.fileDeleteFunction || (async (file: UploadFile) => {
        await fileApi.apiProductImageIdDelete(+file.uid);
    });

    const onPositionChangeClicked = (id: number, direction: Direction) => {
        const temp = props.files[id];
        if (direction === "Down") {
            props.files[id] = props.files[id + 1];
            props.files[id + 1] = temp;
        } else {
            props.files[id] = props.files[id - 1];
            props.files[id - 1] = temp;
        }

        props.setFiles([...props.files]);
    }

    const itemRender: ItemRender<any> = (
        originalItem: React.ReactElement,
        file: UploadFile,
        allFiles: UploadFile<any>[],
        actions) => {
        return <div className={styles.listItem}>
            <Button
                style={allFiles.indexOf(file) === 0 ? {visibility: "hidden"} : {visibility: "visible"}}
                icon={<ArrowUpOutlined/>}
                onClick={() => onPositionChangeClicked!(allFiles.indexOf(file), Direction.Up)}
            />
            <Button
                style={allFiles.indexOf(file) === allFiles.length - 1 ? {visibility: "hidden"} : {visibility: "visible"}}
                icon={<ArrowDownOutlined/>}
                onClick={() => onPositionChangeClicked!(allFiles.indexOf(file), Direction.Down)}
            />
            {originalItem}
        </div>
    }

    const onListChange = (uploadedFileId: string[], allFiles: UploadFile<any>[]) => {
        props.onChange!(uploadedFileId, allFiles);
        props.setFiles(allFiles);
    }

    return <FileUploader
        ref={fileUploaderRef}
        defaultFileList={props.files}
        onChange={onListChange}
        value={props.value}
        accept={props.accept}
        sizeLimitMb={props.sizeLimitMb}
        isFilePublic={props.isFilePublic}
        fileDownloadFunction={fileDownloadFunction}
        fileUploadFunction={fileUploadFunction}
        fileDeleteFunction={fileDeleteFunction}
        fileUploaderProps={{
            itemRender
        }}
        buttonLabel="Kliknij, aby wskazać plik"
        sizeValidationMessage={`Przekroczono maksymalną dopuszczalną wielkość pliku, która wynosi ${props.sizeLimitMb} MB`}
        uploadFilesCountMessage="Można wgrać maksymalnie jeden plik"
    />
});

export interface ProductsFileUploaderProps {
    defaultFileList?: UploadFile<any>[];
    onChange?: (uploadedFileIds?: string[], files?: UploadFile<any>[]) => any;
    value?: number[],
    accept?: string;
    sizeLimitMb?: number,
    useOrganisationFileApi?: boolean,
    isFilePublic?: boolean,
    fileDeleteFunction?: (file: UploadFile) => Promise<void>,
    allFiles?: any,
    files?: any,
    setFiles?: any;
}

export default ProductsImagesUploader;
